import { useState } from "react";
import { Commune } from "../commune-interface";
import { 
  List, 
  ListItem, 
  ListItemText, 
  Box, 
  ToggleButtonGroup, 
  ToggleButton,
  Divider, 
  Chip,
  Typography,
  Tooltip
} from "@mui/material";
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SortByAlphaIcon from '@mui/icons-material/SortByAlpha';
import LocationOnIcon from '@mui/icons-material/LocationOn';

export default function ListComponent({ communes }: { communes: Commune[] }) {
  const [order, setOrder] = useState("time");
  const communesFounds = communes.filter((c) => !!c.timeFound);

  const handleOrderChange = (
    event: React.MouseEvent<HTMLElement>,
    newOrder: string | null
  ) => {
    if (newOrder !== null) {
      setOrder(newOrder);
    }
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box 
        sx={{ 
          p: 2, 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: '#f5f5f5',
          borderRadius: 0
        }}
      >
        <Typography variant="subtitle1">
          {communesFounds.length} {communesFounds.length > 1 ? 'Communes trouvées' : 'Commune trouvée'}
        </Typography>
          <ToggleButtonGroup
          size="small"
          value={order}
          exclusive
          onChange={handleOrderChange}
          aria-label="text order"
        >
          <Tooltip title="Trier par date de découverte">
            <ToggleButton value="time" aria-label="time order">
              <AccessTimeIcon fontSize="small" />
            </ToggleButton>
          </Tooltip>
          <Tooltip title="Trier par ordre alphabétique">
            <ToggleButton value="nom" aria-label="alphabetical order">
              <SortByAlphaIcon fontSize="small" />
            </ToggleButton>
          </Tooltip>
          <Tooltip title="Trier par région">
            <ToggleButton value="region" aria-label="region order">
              <LocationOnIcon fontSize="small" />
            </ToggleButton>
          </Tooltip>
        </ToggleButtonGroup>
      </Box>
      
      <Divider />
      
      <List
        sx={{
          width: '100%',
          bgcolor: 'background.paper',
          overflow: 'auto',
          flex: 1,
          '& .MuiListItem-root': {
            borderBottom: '1px solid rgba(0, 0, 0, 0.05)'
          }
        }}
      >
        {communesFounds.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
            <Typography variant="body1">
              Aucune commune trouvée pour l'instant
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Commencez à jouer en entrant des noms de communes
            </Typography>
          </Box>
        ) : (
          communesFounds
            .sort((a, b) => {
              if (order === "nom") {
                return a.name.localeCompare(b.name);
              }
              if (order === "region") {
                return a.region.localeCompare(b.region) || a.name.localeCompare(b.name);
              }
              // Default is time
              return (b.timeFound?.getTime() || 0) - (a.timeFound?.getTime() || 0);
            })
            .map((commune) => (
              <ListItem key={commune.code} sx={{ py: 1.5 }}>
                <ListItemText 
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body1">{commune.name}</Typography>
                      <Chip 
                        label={commune.region} 
                        size="small"
                        variant="outlined"
                        sx={{ 
                          height: 20, 
                          fontSize: '0.7rem' 
                        }}
                      />
                    </Box>
                  } 
                  secondary={commune.codesPostaux}
                />
              </ListItem>
            ))
        )}
      </List>
    </Box>
  );
}
